import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Loader } from '@melio/penny';

import { completeSso } from '../../api/auth';
import { useAnalytics, type Cta, type PageName, type Flow, type MarketingRegisterSuccessEventName } from '../../analytics';
import { getEntryPoint, getIdentificationMethod } from '../../session';
import { completeAuthRedirect } from '../../hooks/useRedirectParam';
import { redirectSsoError, SSO_CODE_VERIFIER_KEY, SSO_RETURN_PATH_KEY } from '../../hooks/useSsoNavigation';
import { identifyUser, Logger } from '../../monitoring';
import { getSessionStorageItemSafely, isSessionStorageAvailable, removeSessionStorageItemSafely } from '../../utils/storage';
import { dismissDomainTransitionBanner } from '../DomainTransitionBanner/domainTransitionBannerCookie';

const RETURN_PATH_TO_PAGE_NAME: Record<string, PageName> = {
  '/sign-in': 'sign-in-to-melio',
  '/create-account': 'create-your-account',
};

const RETURN_PATH_TO_FLOW: Record<string, Flow> = {
  '/sign-in': 'sign-in',
  '/create-account': 'sign-up',
};

const getAnalyticsContext = (returnPath: string) => {
  const identificationMethod = getIdentificationMethod();

  const pageName: PageName = RETURN_PATH_TO_PAGE_NAME[returnPath] ?? 'sign-in-to-melio';
  const flow: Flow = RETURN_PATH_TO_FLOW[returnPath] ?? 'sign-in';

  const ctaByMethod: Record<string, Cta> = {
    google: 'sign-in-with-google',
    xero: 'sign-in-with-xero',
    intuit: 'sign-in-with-intuit',
  };
  const cta: Cta = (identificationMethod && ctaByMethod[identificationMethod]) ?? 'continue';

  const entryPoint = getEntryPoint();

  return { pageName, flow, entryPoint, identificationMethod, cta };
};

export const SsoCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const { track } = useAnalytics();

  useEffect(() => {
    const handleCallback = async () => {
      const ssoState = searchParams.get('sso_state');
      const error = searchParams.get('error');
      const codeVerifier = getSessionStorageItemSafely(SSO_CODE_VERIFIER_KEY);
      const returnPath = getSessionStorageItemSafely(SSO_RETURN_PATH_KEY) ?? '/sign-in';
      removeSessionStorageItemSafely(SSO_CODE_VERIFIER_KEY);
      removeSessionStorageItemSafely(SSO_RETURN_PATH_KEY);

      const { pageName, flow, entryPoint, identificationMethod, cta } = getAnalyticsContext(returnPath);

      const trackStatus = (statusType: 'success' | 'failure') => {
        track({
          eventName: 'Connection-Status',
          properties: {
            PageName: pageName,
            Flow: flow,
            Intent: flow,
            EntryPoint: entryPoint,
            IdentificationMethod: identificationMethod,
            Cta: cta,
            StatusType: statusType,
          },
        });
      };

      if (error) {
        if (error !== 'access_denied') {
          Logger.error('SSO callback received error from external-tokens', undefined, new Error(error));
        }
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_error', returnPath);
        return;
      }

      if (!ssoState || !codeVerifier) {
        Logger.error(
          'SSO callback missing required parameters',
          { hasSsoState: !!ssoState, hasCodeVerifier: !!codeVerifier, sessionStorageAvailable: isSessionStorageAvailable() },
          new Error('SSO callback missing required parameters')
        );
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_error', returnPath);
        return;
      }

      const result = await completeSso({ ssoState, codeVerifier });

      if (result.type === 'emailNotVerified') {
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_email_not_verified', returnPath);
        return;
      }

      if (result.type === 'error') {
        Logger.error('SSO complete failed', undefined, new Error(result.message));
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_error', returnPath);
        return;
      }

      identifyUser(result.user);
      trackStatus('success');

      if (flow === 'sign-up' && identificationMethod) {
        const marketingEventByMethod: Partial<Record<string, MarketingRegisterSuccessEventName>> = {
          intuit: 'marketing_register_sign-up-with-intuit-success',
          xero: 'marketing_register_sign-up-with-xero-success',
          google: 'marketing_register_sign-up-with-google-success',
        };
        const marketingEvent = marketingEventByMethod[identificationMethod];
        if (marketingEvent) {
          track({ eventName: marketingEvent });
        }
      }

      dismissDomainTransitionBanner();
      completeAuthRedirect();
    };

    void handleCallback();
  }, [searchParams, track]);

  return (
    <Container
      data-testid="sso-callback-page"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
    >
      <Loader />
    </Container>
  );
};
